import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { domainTableQuery } from '../api/overview'
import { CiFormModal } from '../cmdb/CiFormModal'
import { moduleOf, shellQuery } from '../api/shell'
import { AdmPageHead } from '../softsee/adm/AdmPageHead'
import { Content } from '../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { usePageStyles } from '../ui/usePageStyles'
import { companyCell, DOMAIN_COLUMNS, dueCell, nameCell, propCell, servicesCell, type DomainRow } from './columns'

/**
 * Overview::domains - macheta cmdb_records/index.php pentru registrul de
 * domenii. GET /api/v2/overview/domains/table. Butonul "Add domain"
 * (`REC_CAN_EDIT` din macheta, `records.js:123`) deschide formularul CMDB cu
 * clasa registrului deja aleasa - `cmdb/form/0/<clasa>` -> `CiFormModal`.
 */

const STYLES = ['css/adm.css']
const TABLE_ID = 'recTable'
const BLURB =
  'Every domain is a configuration item. "Depended on by" is not a list kept here - it is the depends_on links in the CMDB, the same ones the map and the impact analysis read, so a domain that is about to lapse names the services that lapse with it.'

export function domainColumns(): TableColumn[] {
  const cols: TableColumn[] = [
    { index: 0, title: 'Name', orderable: false, visible: true, html: (row) => nameCell(row as unknown as DomainRow, '/cmdb') },
    { index: 1, title: 'Customer', orderable: false, visible: true, html: (row) => companyCell((row as unknown as DomainRow).company_name) },
  ]
  DOMAIN_COLUMNS.forEach((column, i) => {
    cols.push({
      index: 2 + i,
      title: column.label,
      orderable: false,
      visible: true,
      html: (row) => propCell(row as unknown as DomainRow, column),
    })
  })
  cols.push({
    index: 2 + DOMAIN_COLUMNS.length,
    title: 'Expires',
    orderable: false,
    visible: true,
    /* `adm-nowrap`, ca in `records.js`: "12 Mar 2026 - 14d left" nu se rupe
       peste doua randuri intr-o coloana ingusta. */
    className: 'adm-nowrap',
    html: (row) => dueCell(row as unknown as DomainRow),
    /*
     * Semaforul de pe RANDUL intreg, nu doar pe insigna.
     *
     * `records.js` punea `adm-row-down` pe randurile expirate si
     * `adm-row-degraded` pe cele care expira in 30 de zile - de-aia se vedea
     * dintr-o privire, fara sa citesti coloana. Aici se pierduse cu totul, si
     * un registru de o suta de domenii nu se mai scaneaza cu ochiul.
     */
    rowClass: (row) => {
      const state = (row as unknown as DomainRow).due_state
      if (state === 'expired') return 'adm-row-down'
      if (state === 'soon') return 'adm-row-degraded'
      return undefined
    },
  })
  cols.push({
    index: 3 + DOMAIN_COLUMNS.length,
    title: 'Depended on by',
    orderable: false,
    visible: true,
    html: (row) => servicesCell(row as unknown as DomainRow, '/cmdb'),
  })
  return cols
}

export function DomainsPage() {
  const shell = useQuery(shellQuery(moduleOf('/overview/domains')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const [due, setDue] = useState<'' | 'expired' | 'soon' | 'ok' | 'none'>('')
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 0, dir: 'asc' }, search: '' })
  /*
   * Cautarea asteapta, ca in `records.js`.
   *
   * Acolo era un `typingTimer` de 400 ms inainte de `table.ajax.reload()`.
   * Aici cautarea intra direct in starea tabelului, deci "example.ro" pleca
   * ca zece cereri catre server, una pe litera - si registrul se citeste cu
   * patru interogari pe cerere. Aceeasi asteptare ca in caseta de cautare a
   * lui DataTable, ca sa se poarte la fel.
   */
  const [searchDraft, setSearchDraft] = useState(state.search)
  useEffect(() => {
    if (searchDraft === state.search) return
    const t = window.setTimeout(() => setState({ search: searchDraft, start: 0 }), 400)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft])
  const columns = useMemo(() => domainColumns(), [])
  const [adding, setAdding] = useState(false)
  const table = useQuery({
    ...domainTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      search: state.search,
      due,
      company: 0,
    }),
    placeholderData: keepPreviousData,
  })
  const ready = table.data?.ready !== false
  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  return (
    <Content>
      {!ready ? (
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <h4>
            <i className="fas fa-database"></i> Internet domains
          </h4>
          <p>
            Registry not created yet. It lives in the CMDB as a class of its own, which every service provider gets
            automatically - when the provider is created, and from the ADM run every 15 minutes.
          </p>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            If this message stays, run <code>php bin/console od:provider:defaults</code> on the server; it adds what is
            missing and this screen starts working right away.
          </p>
        </div>
      ) : (
        <>
          {/*
           * MARGINEA PAGINII VINE CU CARTONASUL, NU DINTR-O FOAIE NOUA
           * -----------------------------------------------------------------
           * Ecranul /app/overview/domains statea lipit de marginea ferestrei,
           * singur intre ecranele Overview. De ce, pe rand:
           *
           *   - #page-content are padding: 20px 20px 0 (app.css);
           *   - DataTable pune tk-list-flush pe body cat sta lista pe ecran, iar
           *     list_chrome.css stinge acolo marginea (body.tk-list-flush
           *     #page-content { padding: 0 }) - regula e scrisa pentru ecranele
           *     care SUNT tabelul lor, cap la cap;
           *   - adm.css o da inapoi, dar numai unde e un cartonas de ecran:
           *     body.tk-list-flush #page-content:has(.adm-page-head).
           *
           * Registrul imprumuta foaia (STYLES) pentru pastilele de expirare si
           * randurile terse, dar n-avea cartonas - deci ramanea cap la cap, ca
           * /app/cmdb. Vecinii lui din Overview, ITIL Board si NIS2, incep
           * amandoi cu .adm-page-head, si de-aia ei au spatierea.
           *
           * Deci si-l ia si el, cu componenta scrisa deja (AdmPageHead), si
           * marginea vine odata cu cartonasul. Nicio regula noua, si nimic atins
           * in foile comune: cei 20px scrisi acolo ar muta TOATE listele - CMDB,
           * certificatele, statiile - care in portal chiar stau cap la cap.
           */}
          <AdmPageHead
            tone="blue-hoki"
            icon="fa-globe"
            title="Internet domains"
            note="al cui e fiecare domeniu, ce depinde de el, si cand expira"
          />
          <div className="adm-bar" style={{ marginBottom: 10 }}>
            <div className="adm-field adm-field-wide">
              <label htmlFor="recSearch">Search</label>
              <input
                type="text"
                className="form-control input-sm"
                id="recSearch"
                placeholder="domain, e.g. example.ro"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
            </div>
            <div className="adm-field">
              <label htmlFor="recDue">Expiry</label>
              <select
                className="form-control input-sm"
                id="recDue"
                value={due}
                onChange={(e) => {
                  const v = e.target.value as typeof due
                  setDue(v)
                  setState({ start: 0 })
                }}
              >
                <option value="">Any</option>
                <option value="expired">Expired</option>
                <option value="soon">Expiring in 30 days</option>
              </select>
            </div>
          </div>
          <p className="text-muted small" style={{ margin: '0 0 10px 20px' }}>
            {BLURB}
          </p>
          {table.data?.canEdit && table.data.class && (
            <div style={{ margin: '0 0 10px 20px' }}>
              <button type="button" className="btn btn-success" id="recAdd" onClick={() => setAdding(true)}>
                <i className="fas fa-plus"></i> {table.data.registry?.addLabel ?? 'Add domain'}
              </button>
            </div>
          )}
          {adding && table.data?.class && (
            <CiFormModal
              id={0}
              classId={table.data.class.id}
              onClose={() => setAdding(false)}
              onSaved={() => {
                setAdding(false)
                void table.refetch()
              }}
            />
          )}
          <DataTable
            id={TABLE_ID}
            columnTools
            columns={columns}
            rows={rows}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            error={table.error ? table.error.message : null}
            onRetry={() => void table.refetch()}
            state={state}
            onState={setState}
            rowId={(row) => String(row.DT_RowId ?? `rec_${row.ID}`)}
            /* Copy / Columns / Refresh - `DataTablesProperties.buttons` din
               macheta veche; celelalte liste v4 le au deja. */
            footerButtons={{ copy: true, colvis: true, refresh: () => void table.refetch() }}
          />
        </>
      )}
    </Content>
  )
}
